/**
 * CatalogShelfMapViewer – "View on Shelf Map" integration for Catalog Management.
 *
 * Issue: #226 – CORE – Warehouse Shelf Map – Catalog Primary/Secondary Location Map Integration
 *
 * Allows users to open a shelf-map viewer from a catalog item detail view,
 * highlighting both the primary and secondary location when present.
 *
 * Rules:
 *   - Items with at least one location (primary or secondary) can open the map.
 *   - Both primary and secondary locations are highlighted in the map context.
 *   - Items with no locations show an explicit UX message – no map is launched.
 *   - Map launch is warehouse-scoped. Location IDs are resolved to their parent
 *     warehouse via the shelf_map_slot table to prevent cross-warehouse leakage.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, Map as MapIcon, Room } from '@mui/icons-material';
import {
  useShelfMapLayouts,
  useShelfMapLayout,
} from '@/hooks/queries/shelf-map/useShelfMapLayouts';
import { useMoveShelfMapSlot } from '@/hooks/mutations/shelf-map/useShelfMapMutations';
import { useNotifications } from '@/stores/useNotificationStore';
import { ShelfMapRenderer } from './ShelfMapRenderer';
import { ShelfMapDrawer } from './ShelfMapDrawer';
import { ShelfMapEligibilityGate } from './ShelfMapEligibilityGate';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CatalogItemLocationContext {
  /** UUID of the primary location entity (entity_data.primary_location_id). */
  primaryLocationId: string | null | undefined;
  /** UUID of the secondary location entity (entity_data.secondary_location_id). */
  secondaryLocationId: string | null | undefined;
  /** Display label for the item (nomenclature / display_name). */
  itemName: string;
}

export interface CatalogShelfMapViewerProps {
  locationContext: CatalogItemLocationContext;
  onClose: () => void;
  /**
   * When true, the map auto-scrolls/centers on the item's location on open
   * (#1083). Intended for launch points that open the map from a single,
   * unambiguous asset location context (e.g. Asset Detail View / Search) —
   * leave unset for launch points like catalog item detail, which should
   * keep the default (no-auto-scroll) behavior.
   */
  autoScrollToSelection?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns the set of location IDs that are valid (non-empty). */
const buildLocationSet = (
  primaryId: string | null | undefined,
  secondaryId: string | null | undefined,
): Set<string> => {
  const set = new Set<string>();
  if (primaryId?.trim()) set.add(primaryId.trim());
  if (secondaryId?.trim()) set.add(secondaryId.trim());
  return set;
};

// ---------------------------------------------------------------------------
// Inner map content with catalog location highlighting
// ---------------------------------------------------------------------------

interface CatalogMapContentProps {
  /** Layout to render. */
  layoutId: string;
  /** Set of location IDs to highlight (primary + secondary). */
  highlightedLocationIds: Set<string>;
  /** Label for each highlighted location. */
  locationLabels: Record<string, string>;
  itemName: string;
  autoScrollToSelection?: boolean;
}

const CatalogMapContent: React.FC<CatalogMapContentProps> = ({
  layoutId,
  highlightedLocationIds,
  locationLabels,
  itemName,
  autoScrollToSelection,
}) => {
  const { data: layout, isLoading, isError } = useShelfMapLayout(layoutId);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { mutate: moveSlot } = useMoveShelfMapSlot();
  const notifications = useNotifications();

  const handleSelectSlot = (slotId: string, locationEntityId: string | null) => {
    setSelectedSlotId(slotId);
    setSelectedLocationId(locationEntityId);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedSlotId(null);
    setSelectedLocationId(null);
  };

  const selectedSlot = layout?.slots.find((s) => s.id === selectedSlotId);
  const selectedSlotLabel = selectedSlot?.location_name ?? null;
  const selectedSlotBreadcrumb = selectedSlot?.location_breadcrumb ?? null;
  const selectedSlotAisle = selectedSlot?.aisle_label ?? null;

  const handleMoveSlot = useCallback(
    (slotId: string, targetAisle: string, targetShelf: number) => {
      if (!layout) return;
      moveSlot(
        {
          layoutId: layout.id,
          slotId,
          input: { target_aisle_label: targetAisle, target_shelf_number: targetShelf },
        },
        {
          onError: (err) =>
            notifications.error(err instanceof Error ? err.message : 'Failed to move slot'),
        },
      );
    },
    [layout, moveSlot, notifications],
  );

  if (isLoading) {
    return <Skeleton variant="rounded" height="100%" />;
  }

  if (isError) {
    return <Alert severity="error">Failed to load map layout.</Alert>;
  }

  if (!layout) {
    return null;
  }

  // Derive the initial selection: pre-select the first highlighted location slot
  const firstHighlightedSlot =
    layout.slots.find(
      (s) => s.location_entity_id != null && highlightedLocationIds.has(s.location_entity_id),
    ) ?? null;

  // Auto-open drawer for primary location on initial render
  const autoSlotId = selectedSlotId ?? firstHighlightedSlot?.id ?? null;
  const autoLocationId = selectedLocationId ?? firstHighlightedSlot?.location_entity_id ?? null;

  // Determine which slots are highlighted for this catalog item
  const highlightedSlotIds = new Set(
    layout.slots
      .filter(
        (s) => s.location_entity_id != null && highlightedLocationIds.has(s.location_entity_id),
      )
      .map((s) => s.id),
  );

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Catalog location pin legend */}
      {highlightedLocationIds.size > 0 && (
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Room fontSize="small" color="primary" />
            <Typography variant="caption" color="text.secondary">
              {itemName} locations:
            </Typography>
            {Array.from(highlightedLocationIds).map((locId) => (
              <Chip
                key={locId}
                label={locationLabels[locId] ?? locId.slice(0, 8) + '…'}
                size="small"
                color="primary"
                variant="outlined"
                icon={<Room />}
              />
            ))}
            {firstHighlightedSlot == null && (
              <Chip
                label="Location not mapped to any slot in this layout"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
      )}

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, p: 2, overflow: 'hidden' }}>
          <ShelfMapRenderer
            slots={layout.slots}
            selectedSlotId={autoSlotId}
            highlightedSlotIds={highlightedSlotIds}
            onSelectSlot={handleSelectSlot}
            onMoveSlot={handleMoveSlot}
            autoScrollToSelection={autoScrollToSelection}
          />
        </Box>

        <ShelfMapDrawer
          layoutId={layoutId}
          locationEntityId={autoLocationId}
          slotLabel={selectedSlotLabel}
          slotBreadcrumb={selectedSlotBreadcrumb}
          aisleLabel={selectedSlotAisle}
          open={drawerOpen || (firstHighlightedSlot != null && selectedSlotId === null)}
          onClose={handleCloseDrawer}
        />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Warehouse-scoped layout selector
// ---------------------------------------------------------------------------

interface WarehouseLayoutSelectorProps {
  warehouseEntityId: string;
  warehouseName: string | null;
  locationContext: CatalogItemLocationContext;
  onClose: () => void;
  autoScrollToSelection?: boolean;
}

const WarehouseLayoutSelector: React.FC<WarehouseLayoutSelectorProps> = ({
  warehouseEntityId,
  warehouseName,
  locationContext,
  onClose,
  autoScrollToSelection,
}) => {
  const { data: allLayouts = [], isLoading } = useShelfMapLayouts(warehouseEntityId);
  const activeLayouts = allLayouts.filter((l) => l.is_active);
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null);

  const effectiveLayoutId =
    selectedLayoutId ?? (activeLayouts.length === 1 ? activeLayouts[0]!.id : null);

  const highlightedLocationIds = useMemo(
    () => buildLocationSet(locationContext.primaryLocationId, locationContext.secondaryLocationId),
    [locationContext.primaryLocationId, locationContext.secondaryLocationId],
  );

  // Build location label map from layout slots (resolved when layout loads)
  const locationLabels: Record<string, string> = {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <MapIcon color="primary" fontSize="small" />
          <Typography variant="h6">{locationContext.itemName}</Typography>
          {warehouseName && <Chip label={warehouseName} size="small" variant="outlined" />}
          <Chip label="Catalog Location" size="small" color="primary" />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Layout switcher when multiple active layouts exist */}
          {activeLayouts.length > 1 && (
            <Stack direction="row" spacing={1}>
              {activeLayouts.map((l) => (
                <Button
                  key={l.id}
                  size="small"
                  variant={effectiveLayoutId === l.id ? 'contained' : 'outlined'}
                  onClick={() => setSelectedLayoutId(l.id)}
                >
                  {l.name}
                </Button>
              ))}
            </Stack>
          )}
          <Tooltip title="Close">
            <IconButton onClick={onClose} aria-label="Close catalog map viewer">
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Content */}
      {isLoading && (
        <Box sx={{ flex: 1, p: 2 }}>
          <Skeleton variant="rounded" height="100%" />
        </Box>
      )}

      {!isLoading && activeLayouts.length === 0 && (
        <Box
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}
        >
          <Alert severity="info">
            No published shelf map is available for this warehouse. Contact your administrator to
            publish a map.
          </Alert>
        </Box>
      )}

      {!isLoading && activeLayouts.length > 0 && !effectiveLayoutId && (
        <Box
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}
        >
          <Alert severity="info">Select a map layout above to view the shelf map.</Alert>
        </Box>
      )}

      {!isLoading && effectiveLayoutId && (
        <CatalogMapContent
          key={effectiveLayoutId}
          layoutId={effectiveLayoutId}
          highlightedLocationIds={highlightedLocationIds}
          locationLabels={locationLabels}
          itemName={locationContext.itemName}
          autoScrollToSelection={autoScrollToSelection}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

/**
 * Full-screen catalog shelf-map viewer.
 *
 * The caller is responsible for:
 *   - Resolving the warehouse entity ID for the location(s)
 *   - Confirming at least one location exists before rendering this component
 *
 * This component handles:
 *   - Warehouse-scoped map display
 *   - Highlighting both primary and secondary locations
 *   - Empty states when no map exists or locations are not slotted
 */
export const CatalogShelfMapViewer: React.FC<
  CatalogShelfMapViewerProps & {
    warehouseEntityId: string;
    warehouseName?: string | null;
  }
> = ({ locationContext, warehouseEntityId, warehouseName, onClose, autoScrollToSelection }) => {
  return (
    <Dialog open fullScreen>
      <WarehouseLayoutSelector
        warehouseEntityId={warehouseEntityId}
        warehouseName={warehouseName ?? null}
        locationContext={locationContext}
        onClose={onClose}
        autoScrollToSelection={autoScrollToSelection}
      />
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Launcher button (for use in catalog detail / action bar)
// ---------------------------------------------------------------------------

export interface CatalogShelfMapLauncherProps {
  locationContext: CatalogItemLocationContext;
  /**
   * Warehouse entity ID for the item's locations.
   * Must be provided when a location exists.
   * When null the launcher is disabled and shows a reason.
   */
  warehouseEntityId?: string | null;
  warehouseName?: string | null;
}

/**
 * "View on Shelf Map" button for catalog item detail panels.
 *
 * Disabled with explanatory tooltip when:
 *   - No locations are defined on the item.
 *   - No warehouse entity ID can be resolved.
 */
export const CatalogShelfMapLauncher: React.FC<CatalogShelfMapLauncherProps> = ({
  locationContext,
  warehouseEntityId,
  warehouseName,
}) => {
  const [open, setOpen] = useState(false);

  const hasLocations =
    !!locationContext.primaryLocationId?.trim() || !!locationContext.secondaryLocationId?.trim();

  const disabled = !hasLocations || !warehouseEntityId;

  const tooltipTitle = !hasLocations
    ? 'No locations defined for this catalog item'
    : !warehouseEntityId
      ? 'Warehouse context unavailable'
      : 'View on Shelf Map';

  if (disabled) {
    return (
      <Tooltip title={tooltipTitle}>
        <Box component="span">
          <Button
            variant="outlined"
            size="small"
            startIcon={<MapIcon />}
            disabled
            aria-label="View on Shelf Map"
          >
            View on Shelf Map
          </Button>
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <ShelfMapEligibilityGate
        eligibilityInput={{
          warehouse_entity_id: warehouseEntityId!,
          primary_location_id: locationContext.primaryLocationId,
          secondary_location_id: locationContext.secondaryLocationId,
        }}
        onEligible={() => setOpen(true)}
        renderTrigger={({ onClick, isChecking }) => (
          <Tooltip title="View on Shelf Map">
            <Box component="span">
              <Button
                variant="outlined"
                size="small"
                startIcon={isChecking ? <CircularProgress size={14} /> : <MapIcon />}
                onClick={onClick}
                disabled={isChecking}
                aria-label="View on Shelf Map"
              >
                View on Shelf Map
              </Button>
            </Box>
          </Tooltip>
        )}
      />

      {open && (
        <CatalogShelfMapViewer
          locationContext={locationContext}
          warehouseEntityId={warehouseEntityId!}
          warehouseName={warehouseName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};
